import {
  Button,
  DateRangePicker,
  Icon,
  IconSizeClasses,
  VirtualizedTable,
} from '@eppendorf/vnls-react-components';
import { AuditTrailReportOutput } from '@eppendorf/vnls-reporting-service-utils';
import { createColumnHelper } from '@tanstack/react-table';
import { type ReactElement, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useRouteError } from 'react-router-dom';

import { trackPendoEvent } from '$shared/tracking/trackPendoEvent';
import { formatDateRange } from '$shared/utils/date.utils';

import { CellTimestamp } from '$components/cell-timestamp/cell-timestamp';
import { LoadingErrorHint } from '$components/loading-error-hint/loading-error-hint';
import { SearchInput } from '$components/search-input/search-input';
import { DeleteReportConfirmWrapper } from '$features/report-hub/components/delete-report-confirm-wrapper';
import { ReportListEmptyState } from '$features/report-hub/components/report-list-empty-state';
import { ReportStatusBadge } from '$features/report-hub/components/report-status-badge';
import {
  ReportStatusFilter,
  ReportStatusFilterComponent,
} from '$features/report-hub/components/status-filter';
import {
  useDownloadReport,
  useInfiniteAuditTrailReports,
} from '$features/report-hub/report-hub.api';
import { formatFileSize } from '$features/report-hub/report-hub.utils';
import { useReportListFilters } from '$features/report-hub/use-report-list-filters';

import './audit-report-list.scss';

export function AuditReportList(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    apiStatus,
    apiSearchQuery,
    from,
    to,
    statusFilter,
    searchQuery,
    dateRange,
    selectedQuickOption,
    isDeleteReportDialogOpen,
    selectedReport,
    setIsDeleteReportDialogOpen,
    copiedHashReportId,
    handleSearchQueryChange,
    handleDateRangeChange,
    handleQuickSelectOption,
    handleDeleteReport,
    handleCopyHash,
    handleStatusChange,
    resetSearch,
  } = useReportListFilters();

  const { data, isError, isLoading, isFetching, fetchNextPage, hasNextPage } =
    useInfiniteAuditTrailReports(apiSearchQuery, apiStatus, from, to);

  const downloadReportMutation = useDownloadReport();

  const handleDownloadReport = useCallback(
    async (reportId: string) => {
      try {
        const downloadUrl = await downloadReportMutation.mutateAsync(reportId);
        window.open(downloadUrl, '_blank');
      } catch (error) {
        // Error handling can be added here if needed
        // eslint-disable-next-line no-console -- intentional error logging
        console.error('Failed to download report:', error);
      }
    },
    [downloadReportMutation],
  );

  // Flatten the pages - API returns arrays directly
  const reports = data?.pages?.flatMap((page) => page || []) ?? [];
  const hasActiveFilters =
    searchQuery.trim().length > 0 ||
    statusFilter !== ReportStatusFilter.All ||
    Boolean(dateRange.from) ||
    Boolean(dateRange.to);
  const shouldShowNoDataEmptyState = !hasActiveFilters && reports.length === 0;

  const routeError = useRouteError();

  const columnHelper = useMemo(() => createColumnHelper<AuditTrailReportOutput>(), []);

  const columns = useMemo(
    () => [
      columnHelper.accessor('status', {
        header: () => t('shared.status'),
        meta: {
          dataLabel: t('shared.status'),
        },
        id: 'status',
        // eslint-disable-next-line react/no-unstable-nested-components -- accepted
        cell: (info) => <ReportStatusBadge status={info.getValue()} />,
      }),
      columnHelper.display({
        header: () => t('reportHub.table.reportPeriod'),
        meta: {
          dataLabel: t('reportHub.table.reportPeriod'),
        },
        id: 'reportPeriod',
        // eslint-disable-next-line react/no-unstable-nested-components -- accepted
        cell: (info) => {
          const report = info.row.original;
          return formatDateRange(report.startDate, report.endDate);
        },
      }),
      columnHelper.accessor('requestedBy', {
        header: () => t('reportHub.table.requestedBy'),
        meta: {
          dataLabel: t('reportHub.table.requestedBy'),
        },
        id: 'requestedBy',
        // eslint-disable-next-line react/no-unstable-nested-components -- accepted
        cell: (info) => (
          <span
            className="ellipsize ellipsize--wrap-on-mobile"
            title={info.getValue() ?? ''}
          >
            {info.getValue()}
          </span>
        ),
      }),
      columnHelper.accessor('createdAt', {
        header: () => t('reportHub.table.generated'),
        meta: {
          dataLabel: t('reportHub.table.generated'),
        },
        id: 'generated',
        // eslint-disable-next-line react/no-unstable-nested-components -- accepted
        cell: (info) => {
          const timestamp = info.getValue();
          return timestamp ? <CellTimestamp timestamp={timestamp} /> : '-';
        },
      }),
      columnHelper.accessor('hash', {
        header: () => t('reportHub.table.sha256Hash'),
        meta: {
          dataLabel: t('reportHub.table.sha256Hash'),
        },
        id: 'hash',
        // eslint-disable-next-line react/no-unstable-nested-components -- accepted
        cell: (info) => {
          const hash = info.getValue();
          const report = info.row.original;
          if (!hash) return '-';

          if (copiedHashReportId === report.id) {
            return (
              <div className="flex flex__ai--center gap-s">
                <Icon
                  name="success"
                  size={IconSizeClasses.XSmall}
                  className="bg-green-500 flex"
                />
                <span className="color-green-500 font-weight-bold">Copied</span>
              </div>
            );
          }

          return (
            <div className="flex flex__ai--center gap-s">
              <Button
                size="small"
                variant="icon"
                onClick={(e) => {
                  // Manual Pendo tracking before stopping propagation
                  trackPendoEvent('copy-hash', { reportId: report.id });
                  e.stopPropagation();
                  handleCopyHash(hash, report.id);
                }}
                aria-label="Copy hash"
              >
                <Icon name="document-copy" size={IconSizeClasses.XSmall} />
              </Button>
              <span className="hash-text-truncate">{hash}</span>
            </div>
          );
        },
      }),
      columnHelper.accessor('fileSize', {
        header: () => t('reportHub.table.fileSize'),
        meta: {
          dataLabel: t('reportHub.table.fileSize'),
        },
        id: 'fileSize',
        cell: (info) => formatFileSize(info.getValue()),
      }),
      columnHelper.display({
        header: () => t('reportHub.table.download'),
        meta: {
          dataLabel: t('reportHub.table.download'),
        },
        id: 'download',
        // eslint-disable-next-line react/no-unstable-nested-components -- accepted
        cell: (info) => {
          const report = info.row.original;
          if (report.status === 'Ready') {
            return (
              <Button
                size="small"
                variant="icon"
                onClick={(e) => {
                  // Manual Pendo tracking before stopping propagation
                  trackPendoEvent('download', { reportId: report.id });
                  e.stopPropagation();
                  handleDownloadReport(report.id);
                }}
                aria-label="Download report"
                disabled={downloadReportMutation.isPending}
              >
                <Icon name="save" size={IconSizeClasses.Small} />
              </Button>
            );
          }
          return <span>-</span>;
        },
      }),
      columnHelper.display({
        header: () => t('shared.actions'),
        meta: {
          dataLabel: t('shared.actions'),
        },
        id: 'actions',
        // eslint-disable-next-line react/no-unstable-nested-components -- accepted
        cell: ({ row }) => (
          <Button
            onClick={(e) => {
              handleDeleteReport(e, row.original);
            }}
            variant="icon"
          >
            <Icon name="delete" size={IconSizeClasses.XSmall} />
          </Button>
        ),
      }),
    ],
    [
      copiedHashReportId,
      downloadReportMutation.isPending,
      handleCopyHash,
      handleDownloadReport,
      t,
    ],
  );

  return (
    <>
      <div className="audit-report-list__header">
        <ReportStatusFilterComponent
          selectedStatus={statusFilter}
          onStatusChange={handleStatusChange}
          className="report-status-filter-component"
        />
        <DateRangePicker
          dateRange={{
            from: dateRange.from,
            to: dateRange.to,
          }}
          onDateRangeChange={handleDateRangeChange}
          autoFocus
          disabled={{
            after: new Date(),
          }}
          quickSelect={{
            enabled: true,
            onOptionSelect: handleQuickSelectOption,
            selectedOption: selectedQuickOption,
          }}
        />
        <SearchInput
          onSearchQueryChange={handleSearchQueryChange}
          placeholder={t('reportHub.search.placeholder')}
          id="audit-report-search"
          value={searchQuery}
          className="audit-report-search bg-white"
        />
      </div>

      {(routeError || isError) && <LoadingErrorHint />}

      {!isLoading && !isError && !shouldShowNoDataEmptyState && (
        <div
          className="flex flex__dir--column"
          style={{
            height: reports && reports.length > 0 ? '100vh' : '50vh',
          }}
        >
          <VirtualizedTable
            className="audit-report-table"
            data={reports}
            columns={columns}
            loadMore={() => {
              if (hasNextPage && !isFetching) {
                fetchNextPage();
              }
            }}
            isLoading={isFetching}
            onResetSearch={resetSearch}
            isTableToCardDesign
          />
        </div>
      )}

      {!isLoading && !isError && shouldShowNoDataEmptyState && (
        <ReportListEmptyState
          title={t('reportHub.emptyState.auditTrail.title')}
          description={t('reportHub.emptyState.auditTrail.description')}
          actionLabel={t('reportHub.emptyState.auditTrail.action')}
          onActionClick={() => navigate('/audit-events')}
        />
      )}

      <DeleteReportConfirmWrapper
        isDeleteConfirmOpen={isDeleteReportDialogOpen}
        onOpenChange={setIsDeleteReportDialogOpen}
        selectedReport={selectedReport}
      />
    </>
  );
}
